import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperClass } from 'swiper/types';
import { FreeMode, Thumbs, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";
import "swiper/css/navigation";
import { ProductData } from "../../types/product";
import { getProduct, getProductById } from "../../services/admin/product";

const ProductDetailsPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [swiperDirection, setSwiperDirection] = useState<'vertical' | 'horizontal'>('vertical');
  const [product, setProduct] = useState<ProductData | null>(null);
  const [products, setProducts] = useState<ProductData[]>([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [animateOut, setAnimateOut] = useState(false);

  const openVideoModal = () => setShowVideoModal(true);
  const handleClose = () => {
    setAnimateOut(true);
    setTimeout(() => {
      setShowVideoModal(false);
      setAnimateOut(false);
    }, 300);
  };

  const prevRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSwiperDirection('horizontal');
      } else {
        setSwiperDirection('vertical');
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      const res = await getProduct();
      if (res.success) {
        setProducts(res.data.slice(0, 5)); // Take only top 5
      } else {
        console.error(res.error);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      const result = await getProductById(parseInt(id));
      if (result.success) setProduct(result.data);
    };
    fetchData();
  }, [id]);

  if (!product) return <div className="text-center py-20">Loading product...</div>;

  const handleDownload = (filePath: string) => {
    const url = `${filePath}`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filePath.split("/").pop() || "datasheet");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const allImages = [
    product.product_main_image,
    ...(product.product_image || []),
  ];

  return (
    <>
      {/* Breadcrumb */}
      <section className="pt-5 pb-8">
        <div className="w-full max-w-[1760px] mx-auto px-4">
          <div className="flex flex-wrap items-center -mx-4">
            <div className="w-[70%] sm:w-1/2 max-w-full px-4">
              <nav className="text-xs sm:text-lg font-light !leading-[1.5] text-[#020303]">
                <ol className="list-reset flex">
                  <li>
                    <Link to="/" className="font-medium text-[#009FE3] hover:underline transition-all duration-300 ease-in-out">
                      Home
                    </Link>
                  </li>
                  <li>
                    <span className="mx-2">/</span>
                  </li>
                  <li>
                    <Link to="/products" className="font-medium text-[#009FE3] hover:underline transition-all duration-300 ease-in-out">
                      Products
                    </Link>
                  </li>
                  <li>
                    <span className="mx-2">/</span>
                  </li>
                  <li>
                    <span>Product Details</span>
                  </li>
                </ol>
              </nav>
            </div>
            <div className="w-[30%] sm:w-1/2 max-w-full px-4">
              <button onClick={() => navigate(-1)} className="flex justify-end items-center gap-x-4 text-xs sm:text-lg font-light !leading-[1.5] text-[#020303] hover:text-[#009fe3] transition-all duration-300 ease-in-out ml-auto"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 7H0.960657" stroke="#020303" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M6.98047 1L0.960797 7L6.98047 13" stroke="#020303" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg> <span className="sm:hidden inline-block">Back</span><span className="hidden sm:inline-block">Back to Previous Page</span></button>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-12 sm:pb-24 lg:pb-36">
        <div className="w-full max-w-[1760px] mx-auto px-4">
          <div className="flex flex-wrap -mx-4">
            <div className="w-full lg:w-1/2 max-w-full px-4 lg:mb-0 mb-10">
              <div className="flex flex-col md:flex-row gap-x-2.5 justify-center">
                <div className="w-full md:w-1/6 md:order-1 order-2 md:max-w-[100px] md:max-h-[550px] relative group">
                  <Swiper
                    onSwiper={setThumbsSwiper}
                    direction={swiperDirection}
                    breakpoints={{ 0: { slidesPerView: 4 }, 768: { slidesPerView: 5 } }}
                    freeMode
                    watchSlidesProgress
                    modules={[FreeMode, Thumbs, Navigation]}
                    className="h-full thumbnail-swiper"
                    navigation={{ prevEl: prevRef.current, nextEl: nextRef.current }}
                    onInit={(swiper: SwiperClass) => {
                      if (swiper.params.navigation && typeof swiper.params.navigation !== 'boolean') {
                        swiper.params.navigation.prevEl = prevRef.current;
                        swiper.params.navigation.nextEl = nextRef.current;
                      }
                      swiper.navigation.init();
                      swiper.navigation.update();
                    }}
                  >
                    {allImages.map((img, index) => (
                      <SwiperSlide key={index} className="max-w-[100px]">
                        <div className="w-20 h-20 sm:w-[100px] sm:h-[100px] border bg-[#D9D9D9] cursor-pointer hover:border-[#009FE3] flex items-center justify-center">
                          <img
                            src={`${img}`}
                            alt={`Thumbnail ${index}`}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div ref={prevRef} className={`border border-[#f5f5f5] absolute z-10 flex items-center justify-center p-2.5 bg-white text-[#009FE3] cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 [&.swiper-button-disabled]:!opacity-0 ${swiperDirection === 'vertical' ? 'top-0 left-0 w-full' : 'top-0 left-0 h-full w-auto'}`}>
                    {swiperDirection === 'vertical' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                    )}
                  </div>
                  <div ref={nextRef} className={`border border-[#f5f5f5] absolute z-10 flex items-center justify-center p-2.5 bg-white text-[#009FE3] cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-300 [&.swiper-button-disabled]:!opacity-0 ${swiperDirection === 'vertical' ? 'bottom-0 left-0 w-full' : 'top-0 right-0 h-full w-auto'}`}>
                    {swiperDirection === 'vertical' ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    )}
                  </div>
                </div>
                <div className="w-full md:w-5/6 max-w-full md:order-2 order-1 md:mb-0 mb-5 border border-[#D9D9D9] ">
                  <Swiper thumbs={{ swiper: thumbsSwiper }} modules={[Thumbs]} className="w-full">
                    {allImages.map((img, index) => (
                      <SwiperSlide
                        key={index}
                        className="bg-[#F2F4F6] !flex items-center justify-center !m-0 !w-full md:min-h-[550px] 2xl:min-h-[650px]"
                      >
                        <img
                          src={`${img}`}
                          alt={`Product view ${index + 1}`}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              </div>
            </div>
            <div className="w-full lg:w-1/2 max-w-full px-4">
              <div className="max-w-[690px]">
                <h2 className="text-[30px] lg:text-[40px] font-semibold leading-[1.35] text-[#020303] mb-4 sm:mb-5">{product.product_name}</h2>
                <p className="text-[18px] sm:text-[20px] lg:text-[22px] xl:text-[24px] font-light !leading-[1.5] text-[#020303] mb-[5px]">SKU</p>
                <div className="text-[30px] lg:text-[40px] font-semibold leading-[1.35] text-[#020303] mb-4 sm:mb-5 capitalize">{product.sku}</div>
                <div className="text-xl lg:text-[26px] font-medium leading-8 lg:leading-10 text-[#000000] mb-[5px]">Product description</div>
                <div
                  className="text-[18px] sm:text-[20px] lg:text-[22px] font-light !leading-[1.5] text-[#020303] mb-5 sm:mb-7 md:mb-10 lg:mb-[50px]"
                  dangerouslySetInnerHTML={{ __html: product.short_description || "" }}
                />
                <div className="flex flex-wrap items-center gap-5">
                  <button
                    onClick={() => product.download_more_info && handleDownload(product.download_more_info)}
                    className={`text-base sm:text-[18px] 2xl:text-[22px] font-light !leading-[1.5] border py-3 px-5 xl:px-[30px] transition-all duration-300 ease-in-out text-[#020303] border-[#020303] hover:text-[#FFFFFF] hover:bg-[#009FE3] hover:border-[#009FE3] ${product.download_more_info ? "" : "cursor-not-allowed"
                      }`}
                  >
                    More Information
                  </button>
                  <button onClick={product.product_video ? openVideoModal : undefined} disabled={!product.product_video} className={`flex justify-center items-center gap-3 text-base sm:text-[18px] 2xl:text-[22px] font-semibold !leading-[1.5] py-[8px] sm:py-[10px] 2xl:py-[13px] px-5 transition-all duration-300 ease-in-out text-[#ffffff] bg-[#009FE3] hover:bg-[#020303] ${product.product_video ? "" : "cursor-not-allowed"}`} > <svg className="max-w-[30px] 2xl:max-w-[33px]" width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg"> <path opacity="0.9" d="M16.5 33C25.6127 33 33 25.6127 33 16.5C33 7.3873 25.6127 0 16.5 0C7.3873 0 0 7.3873 0 16.5C0 25.6127 7.3873 33 16.5 33Z" fill="white" /> <path d="M12.5403 12.9163C12.5403 11.3313 14.2961 10.3761 15.6268 11.2371L21.1652 14.8209C22.3832 15.609 22.3832 17.391 21.1652 18.1791L15.6268 21.7628C14.2961 22.6239 12.5403 21.6687 12.5403 20.0837V12.9163Z" fill="#009FE3" /> </svg> View Product Video </button>



                  {product.download_datasheet && (
                    <button
                      onClick={() => product.download_datasheet && handleDownload(product.download_datasheet)}
                      className="text-base sm:text-[18px] 2xl:text-[22px] font-light !leading-[1.5] border py-3 px-5 xl:px-[30px] transition-all duration-300 ease-in-out text-[#020303] border-[#020303] hover:text-[#FFFFFF] hover:bg-[#009FE3] hover:border-[#009FE3]"
                    >
                      Datasheet (PDF)
                    </button>
                  )}

                  {product.download_certificates && (
                    <button
                      onClick={() => product.download_certificates && handleDownload(product.download_certificates)}
                      className="text-base sm:text-[18px] 2xl:text-[22px] font-light !leading-[1.5] border py-3 px-5 xl:px-[30px] transition-all duration-300 ease-in-out text-[#020303] border-[#020303] hover:text-[#FFFFFF] hover:bg-[#009FE3] hover:border-[#009FE3]"
                    >
                      Certificates (PDF)
                    </button>
                  )}

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="pt-12 pb-9 lg:pt-24 lg:pb-20">
        <div className="w-full max-w-[1760px] mx-auto px-4">
          <div className="flex flex-wrap items-center -mx-4 mb-12">
            <div className="w-full lg:w-1/2 max-w-full px-4 text-center lg:text-left mb-4 lg:mb-0">
              <h2 className="text-[30px] lg:text-[42px] 2xl:text-[50px] font-semibold leading-normal text-[#020303]">Trending Items You'll Love</h2>
            </div>
            <div className="w-full lg:w-1/2 max-w-full px-4 text-center lg:text-right">
              <Link to="/products" className="group relative inline-flex items-center justify-center gap-x-5 pb-3 lg:pb-4 border-b-[3px] border-[#009FE3] transition-all duration-300">
                <span className="text-[20px] lg:text-[24px] xl:text-[26px] font-normal !leading-normal text-[#020303] uppercase transition-colors duration-300 group-hover:text-[#005f9e]">View All</span>
                <div className="group w-8 h-8 relative inline-block overflow-hidden">
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16" cy="16" r="15.5" stroke="#141613" /></svg>
                  <svg className="absolute inset-0 w-full h-full transition-all duration-500 group-hover:translate-x-8 group-hover:opacity-0" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M10 16H22" stroke="#009FE3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M16 10L22 16L16 22" stroke="#009FE3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></g></svg>
                  <svg className="absolute inset-0 w-full h-full -translate-x-8 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M10 16H22" stroke="#009FE3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M16 10L22 16L16 22" stroke="#009FE3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></g></svg>
                </div>
              </Link>
            </div>
          </div>
          <div className="flex flex-wrap -mx-3">
            {products.map((product, index) => (
              <div key={index} className="w-full sm:w-1/2 md:w-1/3 2xl:w-1/5 px-3">
                <div className="flex flex-col justify-between items-center border border-[#C3C3C3] w-full h-[calc(100%-24px)] hover:border-[#009FE3] mb-6 group transition-all duration-300 ease-in-out">
                  {product.product_main_image && (
                    <img
                      src={`${product.product_main_image}`}
                      alt={product.product_name}
                      width="262"
                      height="233"
                      className="h-auto w-full max-h-[260px] object-cover"
                    />
                  )}
                  <div className="w-full  p-4">
                    <Link
                      to={`/products/product-details/${product.id}`}
                      data-discover="true"
                      className="block w-full text-[18px] font-medium !leading-[1.5] text-[#020303] 
                             group-hover:text-[#009FE3] transition-all duration-300 ease-in-out 
                             text-center p-4"
                    >
                      {product.product_name.split(" ").slice(0, 5).join(" ") +
                        (product.product_name.split(" ").length > 5 ? "..." : "")}
                    </Link>
                    <div className="text-[16px] font-light !leading-[1.5] text-[#020303] text-center">
                      SKU: <span className="font-bold">{product.sku}</span>
                    </div>
                  </div>
                  <Link to={`/products/product-details/${product.id}`} className="text-center text-base font-normal leading-normal text-[#FFFFFF] bg-[#009FE3] border-2 border-[#009FE3] py-[5px] px-[20px] hover:border-[#020303] hover:bg-[#020303] transition-all duration-300 ease-in-out mb-6 min-w-[169px]">View Details</Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {showVideoModal && (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className={`absolute inset-0 bg-black bg-opacity-70 transition-opacity duration-300 ${animateOut ? 'opacity-0' : 'opacity-100'}`} onClick={handleClose}></div>
            <div className={`relative w-full max-w-4xl z-10 ${animateOut ? 'animate-slideFadeOut' : 'animate-slideFadeIn'}`}>
              <div className="relative bg-white rounded-lg overflow-hidden aspect-video shadow-lg">
                <iframe
                  src={`${product.product_video}`}
                  title="Product Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
                <button onClick={handleClose} className="absolute top-3 right-3 text-white bg-[#009FE3] rounded-full w-8 h-8 flex items-center justify-center text-xl">&times;</button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
export default ProductDetailsPage;
